import { getQuarter } from 'date-fns';
import i18next, { StringMap, TOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import React from 'react';
import {
    DefaultNamespace,
    initReactI18next,
    Namespace,
    TFuncKey,
    Trans as TransBase,
    TransProps,
    useTranslation,
    UseTranslationOptions,
} from 'react-i18next';

if (process.env.NODE_ENV !== 'test') i18next.use(HttpBackend);
i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
        ns: ['common', 'dashboard', 'global', 'inspection-creation', 'project', 'asset-list'],
        fallbackLng: 'en',
        load: 'languageOnly',
        defaultNS: 'common',
        fallbackNS: 'common',
        contextSeparator: '>',
        pluralSeparator: '#',
        lng: process.env.NODE_ENV === 'test' ? 'dev' : undefined,
        debug: process.env.NODE_ENV === 'development', // debug is false in 'test' mode due to console.log pollution
        interpolation: {
            escapeValue: false,
            format: (value, format, lng) => {
                if (!format || !lng) return value;
                if (!(format in formatters)) return `# unknown format ${format} # ${value}`;
                return formatters[format](value, lng);
            },
        },
    });

const formatters: { [format: string]: (value: any, lng: string) => string } = {
    'number-floor': (v, l) => Math.floor(+v).toString(),
    'number-ceil': (v, l) => Math.ceil(+v).toString(),
    'number-round': (v, l) => Math.round(+v).toString(),
    'string-upper': (v, l) => String(v).toUpperCase(),
    'string-lower': (v, l) => String(v).toLowerCase(),
    'date-week': (v, l) => Intl.DateTimeFormat(l, { weekday: 'long' }).format(v),
    'date-week-short': (v, l) => Intl.DateTimeFormat(l, { weekday: 'short' }).format(v),
    'date-day': (v, l) => Intl.DateTimeFormat(l, { day: '2-digit' }).format(v),
    'date-month': (v, l) => Intl.DateTimeFormat(l, { month: 'long' }).format(v),
    'date-month-short': (v, l) => Intl.DateTimeFormat(l, { month: 'short' }).format(v),
    'date-year': (v, l) => Intl.DateTimeFormat(l, { year: 'numeric' }).format(v),
    'date-month-year': (v, l) => `${Intl.DateTimeFormat(l, { month: 'long', year: 'numeric' }).format(v)}`,
    'date-month-year-short': (v, l) => `${Intl.DateTimeFormat(l, { month: 'short', year: 'numeric' }).format(v)}`,
    'date-quarter-year': (v, l) => `${Intl.DateTimeFormat(l, { year: 'numeric' }).format(v)} - Q${getQuarter(v)}`,
    'date-text-long': (v, l) =>
        Intl.DateTimeFormat(l, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(v),
    'date-text-medium': (v, l) => Intl.DateTimeFormat(l, { day: 'numeric', month: 'long', year: 'numeric' }).format(v),
    'date-text-small': (v, l) => Intl.DateTimeFormat(l, { day: 'numeric', month: 'short', year: 'numeric' }).format(v),
    'date-text-smaller': (v, l) => Intl.DateTimeFormat(l, { day: 'numeric', month: 'short' }).format(v),
    'date-number-long': (v, l) =>
        Intl.DateTimeFormat(l, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(v),
    'date-number-medium': (v, l) =>
        Intl.DateTimeFormat(l, { day: '2-digit', month: '2-digit', year: '2-digit' }).format(v),
    'date-number-small': (v, l) =>
        Intl.DateTimeFormat(l, { day: 'numeric', month: 'numeric', year: '2-digit' }).format(v),
    'date-time': (v, l) => Intl.DateTimeFormat(l, { hour: '2-digit', minute: '2-digit' }).format(v),
};

/**
 * Extension of i18next's `useTranslation` hook.
 * This implementation allows the caller to set prefixes for `t` keys, creating a second hierarchy level inside a
 * namespace.
 * Prefixes are not added for fallback and default namespaces, the namespace has to be in the `ns` list for the prefix
 * to be inserted.
 * This hook also provides a `Trans` component extension with the same prefix properties of the `t` function.
 *
 * Examples:
 *
 * Default API:
 * ```tsx
 * import { Trans, useTranslation } from 'react-i18next'
 *
 * const Component = () => {
 *     const { t } = useTranslation(['ns1', 'ns2'])
 *
 *     return <div>
 *         {t('some.key.inside.key1')}
 *         {t('ns1:some.key.inside.key1')}
 *         {t('ns2:some.key.in.key2')}
 *         <Trans ns='ns1' i18nKey='some.key.inside.anotherKey1' />
 *         <Trans ns='ns2' i18nKey='some.key.in.anotherKey2' />
 *         <Trans ns='ns3' i18nKey='key3' />
 *     </div>
 * }
 * ```
 *
 * With extensions:
 * ```tsx
 * import { usePrefixTranslation } from './i18n' // this file
 *
 * const Component = () => {
 *     const { t, Trans } = usePrefixTranslation(['ns1', 'ns2', 'ns3'], { ns1: 'some.key.inside', ns2: some.key.in })
 *     // Trans component provided by usePrefixTranslation
 *
 *     return <div>
 *         {t('key1')} // key1 -> ns1:some.key.inside:key1
 *         {t('ns1:key1')} // ns1:key1 -> ns1:some.key.inside:key1
 *         {t('ns2:key2')} // ns2:key2 -> ns2:some.key.in:key2
 *         <Trans i18nKey='anotherKey1' /> // anotherKey1 -> ns1:some.key.inside:anotherKey1
 *         <Trans i18nKey='ns1:anotherKey1' /> // ns1:anotherKey1 -> ns1:some.key.inside:anotherKey1
 *         <Trans i18nKey='ns2:anotherKey2' /> // ns2:anotherKey2 -> ns1:some.key.in:anotherKey2
 *         <Trans i18nKey='ns3:key3' /> // no prefix associated with ns3
 *     </div>
 * }
 * ```
 *
 * @param ns namespace names
 * @param prefixes object containing prefixes for each namespace
 * @param options options for `useTranslation`
 * @returns object containing the `t` function, `i18n` instance, if it is `ready` and a `Trans` component
 */
export const usePrefixTranslation = (
    ns?: string | string[],
    prefixes?: { [namespace: string]: string },
    options?: UseTranslationOptions,
) => {
    const { t: tBase, i18n, ready } = useTranslation(ns, options);

    /**
     * Check if `key` has any prefix associated with its namespace and add it to the key.
     *
     * @param key  base key
     * @param options t options, this function does not support `options.ns` overrides
     * @returns prefixed key
     */
    const prefixKey = (key: string, options?: TOptions<StringMap> & { ns?: never }) => {
        if (ns == undefined) return key;
        const nsSeparator = options?.nsSeparator || i18n.options.nsSeparator || ':';
        const keySeparator = options?.keySeparator || i18n.options.keySeparator || '.';
        const [partA, partB] = key.split(nsSeparator);
        const namespace = partB != undefined ? partA : ns instanceof Array ? ns[0] : ns;
        const prefix = prefixes?.[namespace];
        return `${namespace}${nsSeparator}${prefix ?? ''}${prefix ? keySeparator : ''}${partB ?? partA}`;
    };

    /**
     * New t function which checks if keys have prefixes associated with it.
     *
     * @param key a single key or a list of keys
     * @param options t options, this function does not support `options.ns` overrides
     * @returns t result
     */
    const t = (key: string | string[], options?: TOptions<StringMap> & { ns?: never }) =>
        tBase(
            (key instanceof Array ? key : [key]).map(key => prefixKey(key, options)),
            options,
        );

    /**
     * Extended trans component with inherits key prefixing properties.
     * Because of that, it cannot override the t function, the i18n instance and the namespaces.
     *
     * @param props trans props
     */
    const Trans = <
        K extends TFuncKey<N> extends infer A ? A : never,
        N extends Namespace = DefaultNamespace,
        E extends Element = HTMLDivElement
    >(
        props: TransProps<K, N, E> & { t?: never; i18n?: never; ns?: never },
    ) => React.createElement(TransBase as any, { ...props, t, i18n, ns });

    return { t, Trans, i18n, ready };
};

export default i18next;
